import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Package, CheckCircle, Truck } from "@phosphor-icons/react";

/**
 * ConfirmReceiptDialog — doctor (or clinic admin) confirms the shipped
 * aligner box arrived at the clinic. Moves case status:
 *   shipped → in_treatment ("Treatment Started").
 *
 * If the doctor doesn't act within 14 days of ship, an admin cron auto-
 * confirms — but the doctor's manual click is the canonical path.
 *
 * Endpoint: POST /api/cases/{id}/confirm-receipt   body: { note }
 */
export function ConfirmReceiptDialog({ caseId, caseCode, trackingNumber, carrier, open, onClose, onConfirmed }) {
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);

    const confirm = async () => {
        setBusy(true);
        try {
            await api.post(`/cases/${caseId}/confirm-receipt`, { note: note.trim() || "" });
            toast.success("Receipt confirmed — treatment started");
            onConfirmed?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to confirm receipt");
        } finally {
            setBusy(false);
        }
    };

    if (!open) return null;
    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-md" data-testid="confirm-receipt-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Package size={20} weight="fill" className="text-emerald-600" />
                        Confirm aligners received
                    </DialogTitle>
                    <DialogDescription>
                        Mark <span className="font-mono font-semibold text-foreground">{caseCode}</span> as received at the clinic. The case will move to "Treatment Started" and the patient timeline will reflect today.
                    </DialogDescription>
                </DialogHeader>

                {(trackingNumber || carrier) && (
                    <div className="rounded-xl border border-border bg-muted/30 p-3 text-xs flex items-start gap-2.5">
                        <Truck size={14} className="text-primary mt-0.5 shrink-0" weight="fill" />
                        <div>
                            <div className="font-semibold text-foreground">{carrier || "Shipment"}</div>
                            {trackingNumber && (
                                <div className="font-mono text-muted-foreground mt-0.5">{trackingNumber}</div>
                            )}
                        </div>
                    </div>
                )}

                <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        Note (optional)
                    </Label>
                    <Textarea
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="e.g. All aligners present, no damage on inspection"
                        rows={2}
                        maxLength={500}
                        data-testid="confirm-receipt-note"
                    />
                    <p className="text-[10px] text-muted-foreground text-right">{note.length}/500</p>
                </div>

                <DialogFooter className="gap-2">
                    <Button variant="outline" onClick={onClose} disabled={busy} className="rounded-full" data-testid="confirm-receipt-cancel">
                        Cancel
                    </Button>
                    <Button
                        onClick={confirm}
                        disabled={busy}
                        className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid="confirm-receipt-submit"
                    >
                        <CheckCircle size={14} className="mr-1.5" weight="fill" />
                        {busy ? "Confirming…" : "Confirm receipt"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default ConfirmReceiptDialog;
